import { useCallback, useMemo } from 'react';

import {
  LoadingOutlined,
  PlusOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { Badge, Empty, Input, Space, Spin, Typography } from 'antd';
import { Link, useHistory, useLocation } from 'react-router-dom';
import './CustomFieldsView.less';

import { useAuth } from '../../../hooks/useAuth';
import { useListFetcher } from '../../../hooks/useListFetcher';
import { useSyncedSearch } from '../../../hooks/useSyncedSearch';
import { useUrlHydration } from '../../../hooks/useUrlHydration';
import { useAppDispatch, useAppSelector } from '../../../store';
import {
  fetchCustomFields,
  hydrateFromUrl,
  searchCustomFieldsByNameWithParams,
  setCustomFieldSearchQuery,
} from '../../../store/features/customFields/customFieldsSlice';
import { ListApiConfig } from '../../../types/list';
import { UserPermissions } from '../../../util';
import { buildListParams } from '../../../util/listApiParams';
import { DrawerHashRoute } from '../../containers/Drawers/types';
import Button from '../../elements/Button';
import NoPermission from '../../elements/NoPermission';
import NoResultsFound from '../../elements/NoResultsFound';
import ViewWrapper from '../../elements/ViewWrapper';
import CustomFieldTableList from './components/CustomFieldTableList';

const { Title } = Typography;

const antSpinIcon = <LoadingOutlined style={{ fontSize: 18 }} spin />;

const CustomFieldsView = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const history = useHistory();
  const { hasPermission } = useAuth();

  const listApiConfig: ListApiConfig = useMemo(
    () => ({
      defaultSort: 'updated_at:desc',
      searchParamKey: 'name',
      searchTransformer: (s) => `*${s.toLowerCase()}*`,
    }),
    []
  );

  const {
    value: customFields,
    searchingCustomFields,
    fetchingCustomFields,
    totalCount,
    page,
    pageSize,
    search: storeSearch,
    sort,
  } = useAppSelector((state) => state.customFields);

  const hasSearchQuery = !!storeSearch;
  const hasNoResults = customFields.length === 0;
  const hasCustomFields = !searchingCustomFields && customFields.length > 0;

  const { hasHydrated } = useUrlHydration({
    location,
    controlsFromStore: { page, pageSize, search: storeSearch, sort },
    onHydrate: (urlControls) => dispatch(hydrateFromUrl(urlControls)),
  });

  const fetchThunk = useCallback(
    (params: Record<string, any>) => dispatch(fetchCustomFields({ params })),
    [dispatch]
  );

  useListFetcher({
    hasHydrated,
    controlsFromStore: { page, pageSize, search: storeSearch, sort },
    config: listApiConfig,
    fetchThunk,
  });

  const hasNoSearchResultsFound = useMemo(
    () => searchingCustomFields === false && hasSearchQuery && hasNoResults,
    [searchingCustomFields, hasSearchQuery, hasNoResults]
  );

  const createNewCustomFieldButton = useMemo(
    () => (
      <Link to={DrawerHashRoute.CustomFieldForm}>
        <Button type="primary" data-cy="create-new-custom-field-btn">
          Create New Custom Field
          <PlusOutlined />
        </Button>
      </Link>
    ),
    []
  );

  const onSearch = useCallback(
    (value: string) => {
      const params = buildListParams(
        { page: 1, pageSize, search: value, sort },
        listApiConfig
      );
      return dispatch(searchCustomFieldsByNameWithParams({ params }));
    },
    [dispatch, pageSize, sort, listApiConfig]
  );

  const { searchValue, onChange, clear } = useSyncedSearch({
    storeSearchValue: storeSearch,
    setStoreSearch: (value) => dispatch(setCustomFieldSearchQuery(value)),
    pageSize,
    sort,
    history,
    location,
    onSearch,
  });

  const headerSectionRight = useMemo(
    () => (
      <Space direction="horizontal" size="middle">
        <Input
          allowClear={!searchingCustomFields}
          value={searchValue}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Search for a field..."
          prefix={<SearchOutlined style={{ color: 'rgba(0,0,0,.45)' }} />}
          suffix={searchingCustomFields && <Spin indicator={antSpinIcon} />}
        />
        {hasPermission(UserPermissions.CustomFieldsCreate) &&
          createNewCustomFieldButton}
      </Space>
    ),
    [
      createNewCustomFieldButton,
      searchValue,
      searchingCustomFields,
      hasPermission,
      onChange,
    ]
  );

  const renderContent = () => {
    if (searchingCustomFields) {
      return (
        <div className="custom-fields-view-container">
          <Spin indicator={antSpinIcon} />
        </div>
      );
    }

    if (hasCustomFields) {
      return (
        <CustomFieldTableList
          loading={fetchingCustomFields}
          customFields={customFields}
        />
      );
    }

    if (hasNoSearchResultsFound) {
      return (
        <NoResultsFound searchQuery={storeSearch} onClear={() => clear()} />
      );
    }

    if (!hasCustomFields) {
      return (
        <div className="custom-fields-view-container">
          <Empty
            description="Currently no custom fields."
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          >
            {hasPermission(UserPermissions.CustomFieldsCreate) &&
              createNewCustomFieldButton}
          </Empty>
        </div>
      );
    }
  };

  if (!hasPermission(UserPermissions.CustomFieldsView)) {
    return <NoPermission />;
  }

  return (
    <ViewWrapper
      headerTitle={
        <Space direction="horizontal">
          <Title level={3}>Custom Fields</Title>
          <Badge
            size="default"
            count={totalCount}
            className="total-count-badge"
          />
        </Space>
      }
      headerSectionRight={headerSectionRight}
    >
      {renderContent()}
    </ViewWrapper>
  );
};

export default CustomFieldsView;
